import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Sparkles, Sun, Send } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import SEO from "../components/SEO";
import { PROPERTY_TYPES } from "../components/quote/QuoteParts";
import { apiClient, formatApiError } from "../lib/api";
import { toast, Toaster } from "sonner";

const STEPS = ["Ville", "Type de bien", "Capacité", "Plage", "Atouts", "Résultat"];

export default function SimulatorPage() {
  const [cities, setCities] = useState([]);
  const [q, setQ] = useState("");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    city_slug: "",
    property_type: "",
    bedrooms: 1,
    capacity: 4,
    distance_beach_m: 500,
    parking: false,
    sea_view: false,
  });
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    apiClient.get("/cities").then((r) => setCities(r.data));
  }, []);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const runSimulation = async () => {
    try {
      const { data } = await apiClient.post("/simulator", form);
      setResult(data);
      next();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const goToQuote = () =>
    navigate(`/devis?ville=${form.city_slug}`, { state: { simulation: { ...form, low: result.low, high: result.high } } });

  const canNext = () => {
    if (step === 0) return !!form.city_slug;
    if (step === 1) return !!form.property_type;
    return true;
  };

  return (
    <PublicLayout>
      <SEO
        title="Simulateur de revenus Airbnb gratuit"
        description="Estimez en 90 secondes les revenus Airbnb de votre bien, partout en Bretagne, puis comparez les conciergeries avec un devis gratuit."
      />
      <Toaster position="top-right" richColors />
      <section className="pt-12 pb-8 md:pt-20 md:pb-12">
        <div className="container-tm max-w-4xl">
          <span className="pill">Simulateur</span>
          <h1 className="h-display mt-5">
            Combien votre bien<br />
            peut-il <span className="text-accent">rapporter ?</span>
          </h1>
          <p className="mt-5 text-muted-foreground text-lg max-w-2xl">
            Estimation gratuite et instantanée, partout en Bretagne.
            Pas d&apos;email demandé pour voir le résultat. 90 secondes chrono
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-tm max-w-3xl">
          {/* Stepper */}
          <div className="flex items-center gap-2 mb-10" data-testid="stepper">
            {STEPS.slice(0, 5).map((label, i) => (
              <div key={label} className="flex-1 flex items-center gap-2">
                <div
                  className={`flex-1 h-1.5 rounded-full transition-colors ${
                    i <= step ? "bg-accent" : "bg-border"
                  }`}
                />
              </div>
            ))}
            <div className="text-sm font-bold text-muted-foreground ml-2 shrink-0">
              {Math.min(step + 1, 5)}/5
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.3 }}
              className="surface-card p-7 md:p-10"
              data-testid={`step-${step}`}
            >
              {step === 0 && (
                <div>
                  <div className="text-xs font-bold text-accent uppercase tracking-widest">Étape 1 / 5</div>
                  <h2 className="h-card mt-2 text-2xl md:text-3xl">Quelle est la ville de votre bien ?</h2>
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher votre commune…" data-testid="sim-city-search"
                    className="mt-6 w-full px-5 py-3.5 border border-input rounded-full bg-background focus:outline-none focus:ring-2 focus:ring-accent/30" />
                  <div className="mt-4 grid sm:grid-cols-2 gap-3 max-h-[440px] overflow-y-auto pr-1">
                    {cities.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())).slice(0, 60).map((c) => (
                      <button
                        key={c.slug}
                        onClick={() => setForm({ ...form, city_slug: c.slug })}
                        data-testid={`city-option-${c.slug}`}
                        className={`text-left p-4 rounded-2xl border-2 transition-all ${
                          form.city_slug === c.slug
                            ? "border-accent bg-accent/5"
                            : "border-border hover:border-accent/50"
                        }`}
                      >
                        <div className="font-semibold">{c.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{c.department}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <div className="text-xs font-bold text-accent uppercase tracking-widest">Étape 2 / 5</div>
                  <h2 className="h-card mt-2 text-2xl md:text-3xl">Quel type de bien ?</h2>
                  <div className="mt-6 flex flex-wrap gap-2" data-testid="sim-property-types">
                    {PROPERTY_TYPES.map((t) => (
                      <button key={t.v} onClick={() => setForm({ ...form, property_type: t.v })} data-testid={`sim-type-${t.v}`}
                        className={`px-5 py-3 rounded-full border-2 text-sm font-semibold transition-colors ${form.property_type === t.v ? "border-accent bg-accent/5 text-accent" : "border-border hover:border-accent/50"}`}>
                        {t.l}
                      </button>
                    ))}
                  </div>
                  <h3 className="h-card mt-8 text-xl md:text-2xl">Combien de chambres ?</h3>
                  <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[0, 1, 2, 3, 4].map((n) => (
                      <button
                        key={n}
                        onClick={() => setForm({ ...form, bedrooms: n })}
                        data-testid={`bedrooms-${n}`}
                        className={`p-5 rounded-2xl border-2 text-center transition-all ${
                          form.bedrooms === n
                            ? "border-accent bg-accent/5"
                            : "border-border hover:border-accent/50"
                        }`}
                      >
                        <div className="font-bold text-3xl tracking-tight" style={{ fontFamily: "Quicksand, sans-serif", letterSpacing: "-0.01em" }}>
                          {n === 0 ? "S" : n}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {n === 0 ? "Studio" : n === 4 ? "4 et +" : `${n} ch.`}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <div className="text-xs font-bold text-accent uppercase tracking-widest">Étape 3 / 5</div>
                  <h2 className="h-card mt-2 text-2xl md:text-3xl">Capacité d&apos;accueil</h2>
                  <p className="text-muted-foreground mt-2 text-sm">Combien de personnes au total ?</p>
                  <div className="mt-10">
                    <input
                      type="range"
                      min="1"
                      max="12"
                      value={form.capacity}
                      onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) })}
                      className="w-full accent-accent"
                      data-testid="capacity-slider"
                    />
                    <div className="mt-6 text-center font-bold text-6xl text-accent" style={{ fontFamily: "Quicksand, sans-serif", letterSpacing: "-0.01em" }}>
                      {form.capacity} <span className="text-base text-muted-foreground font-normal">personne{form.capacity > 1 ? "s" : ""}</span>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <div className="text-xs font-bold text-accent uppercase tracking-widest">Étape 4 / 5</div>
                  <h2 className="h-card mt-2 text-2xl md:text-3xl">Distance à la plage</h2>
                  <p className="text-muted-foreground mt-2 text-sm">À pied, en mètres.</p>
                  <div className="mt-7 grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[
                      { v: 100, l: "< 200 m" },
                      { v: 400, l: "200 – 500 m" },
                      { v: 1000, l: "500 m – 1.5 km" },
                      { v: 3000, l: "1.5 – 5 km" },
                      { v: 8000, l: "> 5 km" },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        onClick={() => setForm({ ...form, distance_beach_m: opt.v })}
                        data-testid={`distance-${opt.v}`}
                        className={`p-4 rounded-2xl border-2 text-center transition-all ${
                          form.distance_beach_m === opt.v
                            ? "border-accent bg-accent/5"
                            : "border-border hover:border-accent/50"
                        }`}
                      >
                        <div className="font-semibold text-sm">{opt.l}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 4 && (
                <div>
                  <div className="text-xs font-bold text-accent uppercase tracking-widest">Étape 5 / 5</div>
                  <h2 className="h-card mt-2 text-2xl md:text-3xl">Atouts de votre logement</h2>
                  <div className="mt-7 space-y-3">
                    {[
                      { key: "sea_view", label: "Vue sur la mer", desc: "Visible depuis le séjour ou la chambre" },
                      { key: "parking", label: "Parking ou garage privé", desc: "Stationnement inclus" },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => setForm({ ...form, [opt.key]: !form[opt.key] })}
                        data-testid={`toggle-${opt.key}`}
                        className={`w-full p-5 rounded-2xl border-2 text-left flex items-center justify-between transition-all ${
                          form[opt.key]
                            ? "border-accent bg-accent/5"
                            : "border-border hover:border-accent/50"
                        }`}
                      >
                        <div>
                          <div className="font-semibold text-lg">{opt.label}</div>
                          <div className="text-sm text-muted-foreground mt-0.5">{opt.desc}</div>
                        </div>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                          form[opt.key] ? "bg-accent text-accent-foreground" : "border-2 border-border"
                        }`}>
                          {form[opt.key] && <Check size={14} strokeWidth={3} />}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 5 && result && (
                <div>
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-bold">
                      <Sparkles size={14} /> Résultat pour {result.city_name}
                    </div>
                    <div className="mt-6 font-bold text-5xl md:text-7xl text-foreground leading-[0.95] tracking-tight" style={{ fontFamily: "Quicksand, sans-serif", letterSpacing: "-0.01em" }}>
                      {result.low.toLocaleString("fr-FR")} –<br />
                      <span className="text-accent">{result.high.toLocaleString("fr-FR")} €</span>
                    </div>
                    <div className="text-muted-foreground mt-3">de revenus locatifs annuels estimés</div>
                    <div className="mt-8 grid grid-cols-2 gap-3 max-w-md mx-auto">
                      <div className="bg-secondary rounded-2xl p-4">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Occupation marché</div>
                        <div className="font-bold text-2xl mt-1">{result.avg_occupancy}%</div>
                      </div>
                      <div className="bg-secondary rounded-2xl p-4">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">€/nuit moyen</div>
                        <div className="font-bold text-2xl mt-1">{result.avg_nightly} €</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-12 pt-10 border-t border-border text-center max-w-xl mx-auto" data-testid="sim-quote-block">
                    <h3 className="h-card text-2xl md:text-3xl">
                      Envie de déléguer ? <span className="text-accent">Comparez les conciergeries</span>
                    </h3>
                    <p className="mt-3 text-sm text-muted-foreground">
                      Une seule demande de devis gratuite : nous vous mettons en relation avec la conciergerie la plus adaptée à votre bien à {result.city_name}.
                    </p>
                    <button onClick={goToQuote} className="btn-accent mt-7" data-testid="sim-quote-cta">
                      <Send size={14} /> Recevoir mon devis conciergerie
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {step < 5 && (
            <div className="flex justify-between mt-8" data-testid="nav-controls">
              <button onClick={prev} disabled={step === 0} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid="prev-step">
                <ArrowLeft size={14} /> Précédent
              </button>
              {step < 4 ? (
                <button onClick={next} disabled={!canNext()} className="btn-primary disabled:opacity-50" data-testid="next-step">
                  Suivant <ArrowRight size={14} />
                </button>
              ) : (
                <button onClick={runSimulation} className="btn-accent" data-testid="run-simulation">
                  <Sun size={14} /> Estimer mes revenus
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
